import { v4 as uuidv4 } from 'uuid';

import ProformaInvoiceEntity, {
  PROFORMA_INVOICE_STATUS,
} from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import ProposalSettings, {
  CustomersDetailsNames,
} from 'src/db/entities/proposalSettings/ProposalSettingsEntity';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';

const defaultCustomerDetails = () =>
  [
    {
      name: CustomersDetailsNames.EMAIL,
      enabled: false,
    },
    {
      name: CustomersDetailsNames.FAX,
      enabled: false,
    },
    {
      name: CustomersDetailsNames.ADDRESS,
      enabled: false,
    },
    {
      name: CustomersDetailsNames.PHONE,
      enabled: false,
    },
  ] as ProposalSettings['customerDetails'];

const defaultBusinessDetails = () => ({
  uuid: uuidv4(),
  imageUrl: null,
  name: null,
  description: null,
  phone: null,
  email: null,
  address: null,
  website: null,
  fax: null,
  moreDetails: null,
});

export const cloneSettingsForInvoice = (settings?: ProposalSettings | null): ProposalSettings => {
  if (settings) {
    return {
      ...settings,
      customerDetails: settings.customerDetails?.length
        ? settings.customerDetails
        : defaultCustomerDetails(),
      businessDetails: settings.businessDetails ?? defaultBusinessDetails(),
    };
  }

  return {
    uuid: uuidv4(),
    workspaceId: null,
    startPrice: 0,
    validity: 0,
    currency: 'USD',
    includesTax: false,
    taxRate: 0,
    enteringPriceIncludesTax: false,
    footerText: '',
    customerDetails: defaultCustomerDetails(),
    includeTotalPrice: true,
    lang: 'en',
    updatedAt: null,
    createdAt: null,
    businessDetails: defaultBusinessDetails(),
  };
};

/**
 * Copy of `settings` (e.g. the workspace quote template) with new ids, to be
 * owned by one invoice. The backend keeps the settings uuid it receives for a
 * new invoice and that uuid is unique, so a reused template uuid would clash
 * with the next invoice created from the same template.
 */
export const cloneSettingsAsNewForInvoice = (
  settings?: ProposalSettings | null
): ProposalSettings => {
  const clone = cloneSettingsForInvoice(settings);

  return {
    ...clone,
    uuid: uuidv4(),
    businessDetails: { ...clone.businessDetails, uuid: uuidv4() },
    createdAt: null,
    updatedAt: null,
  };
};

export const createEmptyDraft = (): ProformaInvoiceDraft => ({
  invoiceId: null,
  number: null,
  priceProposalId: null,
  priceProposalReference: null,
  contactId: null,
  contactName: null,
  title: null,
  description: null,
  discountType: null,
  discountAmount: null,
  items: [{ order: 1, description: '', qty: 1, amount: 0 }],
  subtotalPrice: null,
  priceWithDiscount: null,
  taxValue: null,
  priceWithTax: null,
  dueDate: null,
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  updatedBy: null,
  priceProposalOverrideFields: false,
  priceProposalOverrideSettings: false,
  status: PROFORMA_INVOICE_STATUS.PENDING_PAYMENT,
  settings: cloneSettingsForInvoice(),
});

export const mapEntityToDraft = (entity: ProformaInvoiceEntity): ProformaInvoiceDraft => ({
  uuid: entity.uuid,
  invoiceId: entity.invoiceId,
  number: entity.number,
  priceProposalId: entity.priceProposalId,
  priceProposalReference: entity.priceProposalReference,
  contactId: entity.contactId,
  contactName: entity.contactName,
  title: entity.title,
  description: entity.description,
  discountType: entity.discountType,
  discountAmount: entity.discountAmount,
  items: entity.items,
  subtotalPrice: entity.subtotalPrice,
  priceWithDiscount: entity.priceWithDiscount,
  taxValue: entity.taxValue,
  priceWithTax: entity.priceWithTax,
  dueDate: entity.dueDate,
  createdAt: entity.createdAt,
  updatedAt: entity.updatedAt,
  createdBy: entity.createdBy,
  updatedBy: entity.updatedBy,
  priceProposalOverrideFields: entity.priceProposalOverrideFields ?? false,
  priceProposalOverrideSettings: entity.priceProposalOverrideSettings ?? false,
  status: entity.status,
  settings: cloneSettingsForInvoice(entity.settings),
});
